import React, { useRef, useState } from "react";
import { Download, Upload, FileSpreadsheet, Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import Modal from "./Modal";
import { useData } from "../context/DataContext";
import { readExcelFile, downloadTenantTemplate } from "../utils/excel";
import { formatKES, formatMonth, paidForMonth, currentMonth } from "../utils/format";
import * as XLSX from "xlsx";

export function ExportTenantsButton({ propertyId }) {
  const { tenants, properties, payments } = useData();

  const filtered = tenants.filter((t) => {
    if (t.status !== "ACTIVE") return false;
    if (propertyId) return t.propertyId === propertyId;
    return true;
  });

  const doExport = () => {
    const propMap = Object.fromEntries(properties.map((p) => [p.id, p.name]));
    const prop = propertyId ? properties.find((p) => p.id === propertyId) : null;
    const filename = prop ? `paysafi-${prop.name.toLowerCase().replace(/\s+/g, "-")}-tenants.xlsx` : "paysafi-tenants.xlsx";

    const wb = XLSX.utils.book_new();

    // Tab 1: All tenants
    const tenantRows = filtered.map((t) => ({
      "Name": t.name,
      "Phone": t.phone || "",
      "House Number": t.houseNumber,
      "Property": propMap[t.propertyId] || "",
      "Rent (KES)": Number(t.rent) || 0,
      "Due Day": t.dueDay,
      "Deposit (KES)": Number(t.deposit) || 0,
      "Status": t.status,
    }));
    const wsAll = XLSX.utils.json_to_sheet(tenantRows);
    wsAll["!cols"] = [{ wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 14 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, wsAll, "Tenants");

    // Find all months that have payments + always include current month
    const tenantIds = new Set(filtered.map((t) => t.id));
    const relevantPayments = payments.filter((p) => tenantIds.has(p.tenantId) && (p.chargeType || "rent") === "rent");
    const curMonth = currentMonth();
    const monthSet = new Set(relevantPayments.map((p) => p.month));
    monthSet.add(curMonth);
    const months = [...monthSet].sort().reverse();

    // Tab per month
    for (const month of months) {
      const rows = filtered.map((t) => {
        const rent = Number(t.rent) || 0;
        const paid = paidForMonth(payments, t.id, month);
        const balance = Math.max(0, rent - paid);
        let status = "Unpaid";
        if (paid >= rent && rent > 0) status = "Paid";
        else if (paid > 0) status = "Partial";

        return {
          "Name": t.name,
          "Phone": t.phone || "",
          "House": t.houseNumber,
          "Rent (KES)": rent,
          "Paid (KES)": paid,
          "Balance (KES)": balance,
          "Status": status,
        };
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [{ wch: 20 }, { wch: 15 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 10 }];
      // Sheet name max 31 chars
      const sheetName = formatMonth(month).slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    }

    XLSX.writeFile(wb, filename);
  };

  return (
    <button className="btn-secondary text-sm" onClick={doExport} disabled={filtered.length === 0}>
      <Upload className="h-4 w-4" />
      Export
    </button>
  );
}

export function ImportTenantsButton({ propertyId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="btn-secondary text-sm" onClick={() => setOpen(true)}>
        <Download className="h-4 w-4" />
        Import from Excel
      </button>
      <ImportTenantsModal open={open} onClose={() => setOpen(false)} propertyId={propertyId} />
    </>
  );
}

function ImportTenantsModal({ open, onClose, propertyId }) {
  const { properties, bulkCreateTenants } = useData();
  const fileRef = useRef(null);
  const [phase, setPhase] = useState("upload"); // upload | preview | importing | done
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState({ success: 0, failed: 0, errors: [] });

  const reset = () => {
    setPhase("upload");
    setRows([]);
    setErrors([]);
    setProgress(0);
    setResults({ success: 0, failed: 0, errors: [] });
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const propMap = Object.fromEntries(properties.map((p) => [p.name.toLowerCase().trim(), p]));
  const fixedProperty = propertyId ? properties.find((p) => p.id === propertyId) : null;

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await readExcelFile(file);
      if (data.length === 0) {
        setErrors(["File is empty."]);
        return;
      }

      // Validate and normalize rows
      const parsed = [];
      const errs = [];
      data.forEach((row, i) => {
        const lineNum = i + 2; // Excel row (1-indexed + header)
        const name = String(row["Name"] || row["name"] || "").trim();
        const phone = String(row["Phone"] || row["phone"] || "").trim();
        const house = String(row["House Number"] || row["house number"] || row["House"] || "").trim();
        const propName = String(row["Property Name"] || row["Property"] || row["property name"] || row["property"] || "").trim();
        const rent = Number(row["Rent (KES)"] || row["Rent"] || row["rent"] || 0);
        const dueDay = Number(row["Due Day"] || row["due day"] || row["Due"] || 5);
        const deposit = Number(row["Deposit (KES)"] || row["Deposit"] || row["deposit"] || 0);

        if (!name) { errs.push(`Row ${lineNum}: Name is required`); return; }
        if (!house) { errs.push(`Row ${lineNum}: House Number is required`); return; }

        let prop = fixedProperty;
        if (!prop) {
          if (!propName) { errs.push(`Row ${lineNum}: Property Name is required`); return; }
          prop = propMap[propName.toLowerCase()];
          if (!prop) {
            errs.push(`Row ${lineNum}: Property "${propName}" not found. Create it first.`);
            return;
          }
        }

        parsed.push({ name, phone, houseNumber: house, propertyId: prop.id, propertyName: prop.name, rent, dueDay, deposit });
      });

      setRows(parsed);
      setErrors(errs);
      if (parsed.length > 0) setPhase("preview");
    } catch (err) {
      setErrors([err.message]);
    }
  };

  const startImport = async () => {
    setPhase("importing");
    setProgress(0);

    const tenantsList = rows.map((r) => ({
      name: r.name,
      phone: r.phone,
      houseNumber: r.houseNumber,
      propertyId: r.propertyId,
      rent: r.rent,
      dueDay: r.dueDay,
      deposit: r.deposit,
    }));

    try {
      const data = await bulkCreateTenants(tenantsList);
      setProgress(rows.length);
      setResults({
        success: data.created || 0,
        failed: data.failed || 0,
        errors: data.errors || [],
      });
    } catch (err) {
      setResults({
        success: 0,
        failed: rows.length,
        errors: [err.message || "Bulk import failed"],
      });
    }
    setPhase("done");
  };

  return (
    <Modal open={open} onClose={handleClose} title="Import tenants" description="Upload an Excel or CSV file with tenant data.">
      {phase === "upload" && (
        <div className="space-y-4">
          <div className="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 p-6 text-center">
            <FileSpreadsheet className="mx-auto h-10 w-10 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Upload an Excel (.xlsx) or CSV file
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={onFileChange}
              className="mt-3 block mx-auto text-sm text-gray-500 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-indigo-700 file:cursor-pointer hover:file:bg-indigo-100 dark:file:bg-indigo-950 dark:file:text-indigo-300"
            />
          </div>

          {errors.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {errors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}

          <div className="flex items-center justify-between">
            <button className="text-sm font-medium text-indigo-600 hover:text-indigo-700 cursor-pointer" onClick={downloadTenantTemplate}>
              <Download className="mr-1 inline h-3.5 w-3.5" />
              Download template
            </button>
            <button className="btn-secondary" onClick={handleClose}>Cancel</button>
          </div>

          <div className="rounded-lg bg-gray-50 dark:bg-gray-900 p-3 text-xs text-gray-500 dark:text-gray-400">
            <strong className="text-gray-900 dark:text-white">Required columns:</strong> Name, House Number{!fixedProperty && ", Property Name"}<br />
            <strong className="text-gray-900 dark:text-white">Optional columns:</strong> Phone, Rent (KES), Due Day, Deposit (KES){fixedProperty && ", Property Name"}<br />
            {fixedProperty
              ? `All tenants will be added to ${fixedProperty.name}.`
              : "Property names must match your existing properties exactly."}
          </div>
        </div>
      )}

      {phase === "preview" && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            {rows.length} tenant{rows.length === 1 ? "" : "s"} ready to import
          </div>

          {errors.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
              <div className="flex items-center gap-1 font-medium mb-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                {errors.length} row{errors.length === 1 ? "" : "s"} skipped:
              </div>
              {errors.slice(0, 5).map((e, i) => <div key={i}>{e}</div>)}
              {errors.length > 5 && <div>…and {errors.length - 5} more</div>}
            </div>
          )}

          <div className="max-h-60 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Name</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">House</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Property</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Rent</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Phone</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">{r.name}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{r.houseNumber}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{r.propertyName}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{formatKES(r.rent)}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{r.phone || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={reset}>Back</button>
            <button className="btn-primary" onClick={startImport}>
              <Download className="h-4 w-4" />
              Import {rows.length} tenant{rows.length === 1 ? "" : "s"}
            </button>
          </div>
        </div>
      )}

      {phase === "importing" && (
        <div className="space-y-4 py-4">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              Importing tenants…
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {progress} of {rows.length} completed
            </div>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
            <div
              className="h-full bg-indigo-600 transition-all duration-200"
              style={{ width: `${(progress / Math.max(1, rows.length)) * 100}%` }}
            />
          </div>
        </div>
      )}

      {phase === "done" && (
        <div className="space-y-4">
          {results.failed === 0 ? (
            <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950 p-4">
              <CheckCircle2 className="h-5 w-5 flex-none text-green-600 dark:text-green-400 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-green-800 dark:text-green-300">Import complete</p>
                <p className="mt-1 text-sm text-green-700 dark:text-green-400">
                  {results.success} tenant{results.success === 1 ? "" : "s"} imported successfully.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-4">
              <AlertTriangle className="h-5 w-5 flex-none text-amber-600 dark:text-amber-400 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                  {results.success} imported, {results.failed} failed
                </p>
                {results.errors.slice(0, 5).map((e, i) => (
                  <p key={i} className="mt-1 text-xs text-amber-700 dark:text-amber-400">{e}</p>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <button className="btn-primary" onClick={handleClose}>Done</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
